import { useEffect, useRef, useState } from "react";
import { type NodeDrop, setMoving } from "../lib/dragMove.ts";
import { codeOf, messageOf, useMoveNode } from "../lib/queries.ts";
import { ConfirmDialog } from "./ConfirmDialog.tsx";

type Halt = NodeDrop & { message: string };

/** Runs moves that rows drop onto folders and crumbs, and asks before one shares wider. */
export function MoveLayer() {
  const move = useMoveNode();
  const [widening, setWidening] = useState<Halt | null>(null);
  const [failure, setFailure] = useState<Halt | null>(null);

  const run = (drop: NodeDrop, confirmWidening = false) => {
    setMoving(drop);
    move.mutate(
      {
        id: drop.node.id,
        from: drop.node.parentId,
        parentId: drop.into.parentId,
        ...(confirmWidening ? { confirmWidening: true } : {}),
      },
      {
        onError: (error) => {
          const halt = { ...drop, message: messageOf(error) };
          if (codeOf(error) === "move_widens") setWidening(halt);
          else setFailure(halt);
        },
        onSettled: () => setMoving(null),
      },
    );
  };
  const runRef = useRef(run);
  runRef.current = run;

  useEffect(() => {
    const onMove = (event: Event) => runRef.current((event as CustomEvent<NodeDrop>).detail);
    window.addEventListener("drive:move", onMove);
    return () => window.removeEventListener("drive:move", onMove);
  }, []);

  return (
    <>
      <ConfirmDialog
        open={widening !== null}
        title="This shares it wider"
        confirmLabel={move.isPending ? "Moving…" : "Move anyway"}
        cancelLabel="Keep it where it is"
        pending={move.isPending}
        onCancel={() => setWidening(null)}
        onConfirm={() => {
          if (!widening) return;
          setWidening(null);
          run(widening, true);
        }}
      >
        <p>{widening?.message}</p>
      </ConfirmDialog>
      <ConfirmDialog
        open={failure !== null}
        title={`${failure?.node.name ?? "It"} stayed put`}
        confirmLabel={move.isPending ? "Moving…" : "Try again"}
        cancelLabel="Close"
        pending={move.isPending}
        onCancel={() => setFailure(null)}
        onConfirm={() => {
          if (!failure) return;
          setFailure(null);
          run(failure);
        }}
      >
        <p>
          Moving it into {failure?.into.name} did not work. {failure?.message}
        </p>
      </ConfirmDialog>
    </>
  );
}
